import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

interface SilkRibbonProps {
    className?: string
}

type Point = [number, number]

// The ribbon is a family of cubic curves: spine + (s - 0.5) * width, for s from 0 (one edge) to 1 (the other).
// The width flips direction near the end, so the strands cross and the ribbon appears to twist.
const SPINE: Array<Point> = [[675, -80], [880, 225], [1235, 400], [1325, 1180]]
const WIDTH: Array<Point> = [[790, 0], [560, -40], [0, 150], [-320, -40]]

const STRAND_COUNT = 88

// Brightness across the ribbon, from one edge to the other, so it reads as folded fabric.
const FOLDS = [0.45, 0.85, 1, 0.7, 0.42, 0.62, 0.95, 1, 0.74, 0.5, 0.68]

const GRADIENT_STOPS: Array<[string, string]> = [
    ['0', '#bdd6ff'],
    ['0.16', '#a9b4ff'],
    ['0.29', '#c49cf5'],
    ['0.37', '#ffa04a'],
    ['0.5', '#ff8a1f'],
    ['0.62', '#ff6b6b'],
    ['0.74', '#ff4fa6'],
    ['0.88', '#7d5cff'],
    ['1', '#ff7fcf']
]

const round = (value: number): number => Math.round(value * 100) / 100

const pointAt = (index: number, s: number): string => {
    const offset = s - 0.5
    const x = SPINE[index][0] + WIDTH[index][0] * offset
    const y = SPINE[index][1] + WIDTH[index][1] * offset

    return `${x.toFixed(1)} ${y.toFixed(1)}`
}

const strandPath = (s: number): string =>
    `M${pointAt(0, s)}C${pointAt(1, s)} ${pointAt(2, s)} ${pointAt(3, s)}`

const foldAt = (s: number): number => {
    const position = s * (FOLDS.length - 1)
    const index = Math.min(Math.floor(position), FOLDS.length - 2)
    const t = position - index

    return FOLDS[index] + (FOLDS[index + 1] - FOLDS[index]) * t
}

const STRANDS = Array.from({ length: STRAND_COUNT }, (_, index) => {
    const s = index / (STRAND_COUNT - 1)
    const edge = 4 * s * (1 - s)

    return {
        d: strandPath(s),
        opacity: round(0.1 + 0.68 * edge * foldAt(s)),
        width: round(0.7 + ((index * 7) % 5) * 0.32)
    }
})

const HIGHLIGHTS = [0.4, 0.47, 0.52].map(strandPath)

const OUTLINE = `M${pointAt(0, 0)}C${pointAt(1, 0)} ${pointAt(2, 0)} ${pointAt(3, 0)}L${pointAt(3, 1)}C${pointAt(2, 1)} ${pointAt(1, 1)} ${pointAt(0, 1)}Z`

export const SilkRibbon: FC<SilkRibbonProps> = ({ className }): ReactNode => {
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const gradientId = `silk-gradient-${uid}`
    const sheenId = `silk-sheen-${uid}`
    const softId = `silk-soft-${uid}`
    const glowId = `silk-glow-${uid}`

    return (
        <svg
            className={cn('pointer-events-none block', className)}
            viewBox='0 0 1440 1000'
            preserveAspectRatio='xMidYMid slice'
            fill='none'
            aria-hidden='true'
            focusable='false'
        >
            <defs>
                <linearGradient id={gradientId} gradientUnits='userSpaceOnUse' x1='300' y1='0' x2='1450' y2='900'>
                    {GRADIENT_STOPS.map(([offset, color]) => (
                        <stop key={offset} offset={offset} stopColor={color} />
                    ))}
                </linearGradient>

                <linearGradient id={sheenId} gradientUnits='userSpaceOnUse' x1='0' y1='0' x2='0' y2='760'>
                    <stop offset='0' stopColor='#fff8e1' stopOpacity='0.95' />
                    <stop offset='0.6' stopColor='#ffe2a8' stopOpacity='0.55' />
                    <stop offset='1' stopColor='#ffffff' stopOpacity='0' />
                </linearGradient>

                <filter id={softId} x='-10%' y='-10%' width='120%' height='120%'>
                    <feGaussianBlur stdDeviation='6' />
                </filter>

                <filter id={glowId} x='-30%' y='-30%' width='160%' height='160%'>
                    <feGaussianBlur stdDeviation='42' />
                </filter>
            </defs>

            <path d={OUTLINE} fill={`url(#${gradientId})`} opacity='0.22' filter={`url(#${glowId})`} />
            <path d={OUTLINE} fill={`url(#${gradientId})`} opacity='0.38' filter={`url(#${softId})`} />

            <g stroke={`url(#${gradientId})`} strokeLinecap='round'>
                {STRANDS.map(strand => (
                    <path key={strand.d} d={strand.d} strokeWidth={strand.width} strokeOpacity={strand.opacity} />
                ))}
            </g>

            <g stroke={`url(#${sheenId})`} strokeLinecap='round' strokeWidth='1.4'>
                {HIGHLIGHTS.map(d => (
                    <path key={d} d={d} />
                ))}
            </g>
        </svg>
    )
}
